import { useState } from "react";
import { Button } from "@shop-ledger/ui-components";
import { getEcommerceSettings, updateEcommerceSettings } from "../api/ecommerceSettingsApi";
import { StatusBanner } from "./StatusBanner";
import { ApiError } from "../lib/apiClient";
import { useAsyncData } from "../lib/useAsyncData";
import { useAuth } from "../auth/AuthContext";

/**
 * The online-store on/off switch (`PATCH /tenant-settings/ecommerce`), shared between
 * `BrandingSettingsPage` (where it originally lived) and `DashboardPage` (business-owner
 * request: a new business's storefront defaults on now, but the switch itself was easy to
 * miss buried in Settings — surfacing it on the page everyone already lands on by default
 * means "where do I turn this on" never comes up again). One fetch/mutate implementation
 * so both call sites always agree on the live state instead of drifting.
 */
export function OnlineStoreToggle({ compact = false }: { compact?: boolean }) {
  const { ensureFreshToken } = useAuth();
  const ecommerceState = useAsyncData(() => ensureFreshToken().then((token) => getEcommerceSettings(token)), []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enabled = ecommerceState.data?.ecommerce_enabled ?? null;

  async function handleToggle(nextEnabled: boolean) {
    setError(null);
    setSaving(true);
    try {
      const token = await ensureFreshToken();
      await updateEcommerceSettings(token, { ecommerce_enabled: nextEnabled });
      ecommerceState.reload();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Could not update storefront status.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={compact ? "sl-store-toggle sl-store-toggle--compact" : "sl-store-toggle"}>
      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className={`badge ${enabled ? "badge--current" : "badge--overdue"}`}>
          {enabled === null ? "Loading…" : enabled ? "Online store: Online" : "Online store: Offline"}
        </span>
        <Button type="button" variant="secondary" disabled={enabled === null || saving} onClick={() => void handleToggle(!enabled)}>
          {saving ? "Saving…" : enabled ? "Take offline" : "Bring online"}
        </Button>
      </div>
    </div>
  );
}
